import { CaretRightIcon, GearIcon, SignOutIcon } from "@phosphor-icons/react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { NavLink, useLocation, useParams } from "react-router";

import { Logo, LogoIcon } from "@/components/logo";
import { AboutSystemAlertDialog } from "@/components/navigation/about-system-alert-dialog";
import { DistrictSelector } from "@/components/navigation/district-selector";
import { ResellerSelector } from "@/components/navigation/reseller-selector";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
	Sidebar,
	SidebarContent,
	SidebarFooter,
	SidebarGroup,
	SidebarGroupLabel,
	SidebarHeader,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	SidebarMenuSub,
	SidebarMenuSubButton,
	SidebarMenuSubItem,
	SidebarRail,
	useSidebar,
} from "@/components/ui/sidebar";
import {
	getNavGroupsForAdmin,
	getNavGroupsForRole,
	type NavItem,
} from "@/config/navigation";
import { isValidLang, type Lang, ROUTE_SLUGS } from "@/config/routes";
import { useAuth } from "@/contexts/auth-context";
import { useHideElectricity } from "@/hooks/use-hide-electricity";
import { getUserRole } from "@/types/auth";

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
	const { state } = useSidebar();
	const isCollapsed = state === "collapsed";
	const { user, logout } = useAuth();
	const { t } = useTranslation();

	const { lang } = useParams<{ lang: string }>();
	const currentLang: Lang = lang && isValidLang(lang) ? lang : "pl";
	const role = user ? getUserRole(user) : "end_user";
	const isAdmin = role === "admin";
	const hideElectricity = useHideElectricity();
	const electricitySlug = ROUTE_SLUGS.electricityMeters[currentLang];

	function filterElectricityFromItems(items: NavItem[]): NavItem[] {
		if (!hideElectricity) return items;
		return items.map((i) =>
			i.items
				? {
						...i,
						items: i.items.filter(
							(c) => c.url !== `/${currentLang}/${electricitySlug}`,
						),
					}
				: i,
		);
	}

	const rawGroups = isAdmin
		? getNavGroupsForAdmin(t, currentLang, user)
		: getNavGroupsForRole(t, currentLang, role, user);
	const navGroups = rawGroups.map((g) => ({
		...g,
		items: filterElectricityFromItems(g.items),
	}));
	const { pathname, search } = useLocation();
	const searchParams = React.useMemo(
		() => new URLSearchParams(search),
		[search],
	);
	const currentViewAs = searchParams.get("viewAs");
	const logoSrc = user?.logo || undefined;

	function buildSearch(viewAsRole: string | null) {
		const params = new URLSearchParams();
		const osiedle = searchParams.get("osiedle");
		if (osiedle) params.set("osiedle", osiedle);
		if (viewAsRole) params.set("viewAs", viewAsRole);
		const str = params.toString();
		return str ? `?${str}` : "";
	}

	const districtSearch = buildSearch(null);
	const settingsUrl = `/${currentLang}/${ROUTE_SLUGS.settings[currentLang]}`;
	const isSettingsActive = pathname.startsWith(settingsUrl);

	function renderNavItem(
		item: NavItem,
		itemSearch: string,
		viewAsRole: string | null,
		keyPrefix: string,
	) {
		if (item.items?.length) {
			const isAnyChildActive = item.items.some(
				(c) =>
					c.url &&
					pathname.startsWith(c.url) &&
					(!viewAsRole || currentViewAs === viewAsRole),
			);
			return (
				<Collapsible
					key={`${keyPrefix}-${item.title}`}
					defaultOpen
					className="group/collapsible"
				>
					<SidebarMenuItem>
						<CollapsibleTrigger asChild>
							<SidebarMenuButton
								tooltip={item.title}
								isActive={isAnyChildActive}
							>
								<item.icon aria-hidden="true" />
								<span>{item.title}</span>
								<CaretRightIcon
									aria-hidden="true"
									className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90"
								/>
							</SidebarMenuButton>
						</CollapsibleTrigger>
						<CollapsibleContent>
							<SidebarMenuSub>
								{item.items.map((subItem) => {
									const isSubActive = subItem.url
										? pathname.startsWith(subItem.url) &&
											(!viewAsRole || currentViewAs === viewAsRole)
										: false;
									return (
										<SidebarMenuSubItem key={subItem.url}>
											<SidebarMenuSubButton asChild isActive={isSubActive}>
												<NavLink
													to={{ pathname: subItem.url!, search: itemSearch }}
												>
													<span>{subItem.title}</span>
												</NavLink>
											</SidebarMenuSubButton>
										</SidebarMenuSubItem>
									);
								})}
							</SidebarMenuSub>
						</CollapsibleContent>
					</SidebarMenuItem>
				</Collapsible>
			);
		}

		const pathMatch = item.exact
			? pathname === item.url
			: pathname.startsWith(item.url!);
		const isActive = pathMatch && (!viewAsRole || currentViewAs === viewAsRole);
		return (
			<SidebarMenuItem key={`${keyPrefix}-${item.url}`}>
				<SidebarMenuButton asChild tooltip={item.title} isActive={isActive}>
					<NavLink
						to={{ pathname: item.url!, search: itemSearch }}
						end={item.exact}
					>
						<item.icon aria-hidden="true" />
						<span>{item.title}</span>
					</NavLink>
				</SidebarMenuButton>
			</SidebarMenuItem>
		);
	}

	return (
		<Sidebar collapsible="icon" {...props}>
			<SidebarHeader>
				<SidebarMenu>
					<SidebarMenuItem>
						<SidebarMenuButton
							size="lg"
							asChild
							className="hover:bg-transparent active:bg-transparent hover:text-inherit active:text-inherit data-active:bg-transparent data-active:text-inherit data-active:font-normal"
						>
							<NavLink
								to={{ pathname: `/${currentLang}/`, search: districtSearch }}
							>
								{isCollapsed ? (
									!logoSrc && <LogoIcon className="mx-auto size-5" />
								) : (
									<Logo
										src={logoSrc}
										className={logoSrc ? "h-10 max-w-[140px]" : undefined}
									/>
								)}
							</NavLink>
						</SidebarMenuButton>
					</SidebarMenuItem>
				</SidebarMenu>
			</SidebarHeader>
			<SidebarContent>
				<SidebarGroup>
					<DistrictSelector />
					<ResellerSelector />
				</SidebarGroup>
				{navGroups.map((group, index) => {
					const groupSearch = group.role
						? buildSearch(group.role)
						: districtSearch;
					const groupKey = `${group.role ?? "__group"}-${group.label || index}`;
					return (
						<SidebarGroup key={groupKey}>
							{group.label && (
								<SidebarGroupLabel>{group.label}</SidebarGroupLabel>
							)}
							<SidebarMenu className="gap-0.5">
								{group.items.map((item) =>
									renderNavItem(item, groupSearch, group.role, groupKey),
								)}
							</SidebarMenu>
						</SidebarGroup>
					);
				})}
			</SidebarContent>
			<SidebarFooter>
				{user && (
					<SidebarMenu className="gap-0.5">
						<SidebarMenuItem>
							<AboutSystemAlertDialog role={role} lang={currentLang} />
						</SidebarMenuItem>
						<SidebarMenuItem>
							<SidebarMenuButton
								asChild
								tooltip={t("nav.settings")}
								isActive={isSettingsActive}
							>
								<NavLink to={{ pathname: settingsUrl, search: districtSearch }}>
									<GearIcon aria-hidden="true" />
									<span>{t("nav.settings")}</span>
								</NavLink>
							</SidebarMenuButton>
						</SidebarMenuItem>
						<SidebarMenuItem>
							<SidebarMenuButton
								tooltip={t("nav.logout")}
								onClick={logout}
								className="cursor-pointer"
							>
								<SignOutIcon aria-hidden="true" />
								<span>{t("nav.logout")}</span>
							</SidebarMenuButton>
						</SidebarMenuItem>
					</SidebarMenu>
				)}
				<p className="px-2 text-left text-[10px] text-sidebar-foreground/40 group-data-[collapsible=icon]:hidden">
					v{__APP_VERSION__}
				</p>
			</SidebarFooter>
			<SidebarRail />
		</Sidebar>
	);
}
